<template>
	<div
		v-if="ad && headline && visible"
		class="modal-ad-container"
		style="display: block"
	>
		<!-- Markup mirrors the legacy AdButler modal so the existing global -->
		<!-- styles (close-button positioning, blue footer bar, etc.) still -->
		<!-- apply. Content is now driven by the Aizen ad-manager payload. -->
		<div class="modal-ad">
			<div>
				<span
					class="text-sm text-slate-400 ml-auto mb-4 inline-block adblutler-modal-close"
					role="button"
					tabindex="0"
					@click="dismiss"
					@keydown.enter.prevent="dismiss"
					@keydown.space.prevent="dismiss"
				>
					Close
				</span>

				<h2 class="modal-title leading-none lg:leading-tight">
					{{ headline }}
				</h2>

				<a
					v-if="ad.imageUrl"
					:href="targetHref"
					target="_blank"
					rel="noopener noreferrer sponsored"
					@click="onCtaClick"
				>
					<img
						:src="ad.imageUrl"
						alt="modal-ad"
						class="mb-5"
					/>
				</a>

				<p
					v-if="body"
					class="text-base mb-5 text-center text-slate-600"
				>
					{{ body }}
				</p>

				<a
					:href="targetHref"
					target="_blank"
					rel="noopener noreferrer sponsored"
					class="information-subscribe-button mb-5"
					@click="onCtaClick"
				>
					{{ ctaLabel }}
				</a>
			</div>
		</div>

		<div
			class="ad-modal-overlay"
			@click="dismiss"
		/>
	</div>
</template>

<script setup>
const props = defineProps({
	slotId: {
		type: String,
		default: "11094ff5-2c64-4ac3-99e7-5e9223069723"
	},
	// Delay between ad-payload arrival and showing the modal — matches the
	// previous AdButler modal UX (a few seconds after the page settles).
	showDelayMs: { type: Number, default: 5000 },
	// Auto-close after the modal has been open this long. The previous
	// AdButler modal closed itself after 25s.
	autoCloseMs: { type: Number, default: 25000 }
});

const AD_BASE_URL = "https://aizen-ad-manager.up.railway.app/api/slots";

// Session-scoped dismiss state. No expires / Max-Age means the cookie is
// dropped when the browser session ends, so the visitor sees the modal again
// next session (same lifecycle the AdButler modal aimed for).
const dismissed = useCookie("promo_modal_dismissed", {
	sameSite: "lax",
	path: "/"
});

const route = useRoute();

const visible = ref(false);
const ad = ref(null);
const showTimer = ref(null);
const closeTimer = ref(null);
const impressionFired = ref(false);

const { pageKey } = usePageKey()
const adCtx = computed(() => ({ pageKey: pageKey.value, surface: "modal" }))
const targetHref = computed(() =>
	buildAdClickUrl(ad.value?.clickUrl ?? ad.value?.destinationUrl ?? "#", adCtx.value)
);

// Headline comes from `properties.title` (what the legacy AdButler markup put
// in the <h2>), falling back to the top-level `name`. Trim so a whitespace-only
// value doesn't keep `v-if="ad && headline"` truthy with nothing to render.
const headline = computed(() => {
	const p = ad.value?.properties ?? {};
	const raw = p.title ?? p.Title ?? ad.value?.name ?? "";
	return String(raw).trim();
});

// Body / CTA come from `properties`. Accept multiple casings so the same
// component works if the slot is later edited. Trim everything so a field
// containing only whitespace doesn't render an empty <p> or a label-less CTA.
const body = computed(() => {
	const p = ad.value?.properties ?? {};
	const raw = p.body ?? p.Body ?? p.copy ?? p.Copy ?? "";
	return String(raw).trim();
});
const ctaLabel = computed(() => {
	const p = ad.value?.properties ?? {};
	const raw = String(p.cta_label ?? "").trim();
	return raw || "Learn more";
});

const hasContent = (raw) => String(raw ?? "").trim().length > 0;

const isDismissed = () => !!dismissed.value;
const persistDismiss = () => {
	dismissed.value = "1";
};

// Don't pop the promo modal on news / error routes (mirrors the legacy
// route-exclusion intent in layouts/default.vue).
const isExcludedRoute = () => {
	const name = String(route.name ?? "");
	return name.startsWith("news") || name === "404" || name === "500";
};

const closeModal = () => {
	if (closeTimer.value) {
		clearTimeout(closeTimer.value);
		closeTimer.value = null;
	}
	visible.value = false;
};

// User-initiated dismiss (X / overlay). Persist so we don't re-pop on the next
// page load this session.
const dismiss = () => {
	closeModal();
	persistDismiss();
};

// Click-through is engagement → persist immediately so the modal doesn't
// re-pop after the visitor has clicked the CTA.
const onCtaClick = () => {
	persistDismiss();
	fireAdClickEvent(adCtx.value);
};

const fireImpression = () => {
	if (impressionFired.value) return;
	const url = ad.value?.impressionUrl;
	if (!url) return;
	impressionFired.value = true;
	fetch(url, { keepalive: true }).catch(() => {});
};

const openModal = () => {
	visible.value = true;
	fireImpression();
	closeTimer.value = setTimeout(() => {
		closeModal();
	}, props.autoCloseMs);
};

onMounted(() => {
	if (!process.client) return;
	if (isDismissed() || isExcludedRoute()) return;

	const run = async () => {
		try {
			const response = await $fetch(`${AD_BASE_URL}/${props.slotId}/serve`);
			if (!response) return; // `null` => no ad available
			if (!response.clickUrl && !response.destinationUrl) return;

			// Need a visible headline (title or name).
			const payloadProps = response.properties ?? {};
			const hasHeadline =
				hasContent(payloadProps.title) ||
				hasContent(payloadProps.Title) ||
				hasContent(response.name);
			if (!hasHeadline) return;

			// Refuse a near-empty modal: require at least an image or body copy.
			const hasBody =
				hasContent(payloadProps.body) ||
				hasContent(payloadProps.Body) ||
				hasContent(payloadProps.copy) ||
				hasContent(payloadProps.Copy);
			if (!hasContent(response.imageUrl) && !hasBody) return;

			ad.value = response;
			showTimer.value = setTimeout(() => {
				openModal();
			}, props.showDelayMs);
		} catch (error) {
			console.error("[PromoModalAd] Failed to fetch ad:", error);
		}
	};

	if ("requestIdleCallback" in window) {
		window.requestIdleCallback(run, { timeout: 2000 });
	} else {
		setTimeout(run, 0);
	}
});

onBeforeUnmount(() => {
	if (showTimer.value) {
		clearTimeout(showTimer.value);
		showTimer.value = null;
	}
	closeModal();
});
</script>

<style lang="scss">
.adblutler-modal-close {
	cursor: pointer;
	margin-bottom: 16px;
	margin-left: auto;
	display: block;
	max-width: 40px;
	position: relative;
	text-align: right;
	@include breakpoint(lg) {
		margin-bottom: 24px;
	}
}

.ad-modal-overlay {
	position: absolute;
	height: 100%;
	width: 100%;
	left: 0;
	top: 0;
	z-index: 0;
}
.modal-ad,
.ts-modal {
	min-width: 320px;
	min-height: 200px;
	max-width: 500px;
	padding: 20px 24px 64px;
	overflow: hidden;
	border-radius: 12px;
	background-color: white;
	position: relative;
	z-index: 1;
	left: 0;
	right: 0;
	top: 0;
	bottom: 0;
	margin: auto;
	border: 3px solid $neutral-200;
	@include breakpoint(lg) {
		border: none;
		box-shadow: 0 0 8px rgba(0, 0, 0, 0.25);
		padding: 20px 32px 40px;
	}
	&::before {
		content: "";
		background-color: #1e3a8a;
		height: 24px;
		width: 100%;
		display: block;
		left: 0;
		right: 0;
		bottom: 0;
		position: absolute;
	}
	&::after {
		content: "";
		background-image: url("/mstile-144x144.png");
		height: 32px;
		width: 32px;
		display: block;
		background-size: contain;
		position: absolute;
		left: 0;
		right: 0;
		bottom: 8px;
		outline: 4px solid #1e3a8a;
		border-radius: 32px;
		margin: auto;
	}
	iframe {
		height: 100%;
		width: 100%;
	}

	.mb-5 {
		margin-bottom: 32px;
	}

	img {
		width: 100%;
		border-radius: 12px;
	}
}
.modal-title {
	text-align: center;
	font-size: 20px;
	margin-bottom: 23px;
	font-weight: 700;
	@include breakpoint(lg) {
		font-size: 28px;
		margin-bottom: 24px;
	}
}

.modal-ad-container,
#adbutler-modal-fallback {
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	margin: auto;
	z-index: 9999;
	display: flex;
	padding: 64px 16px;
	background-color: rgba($color: #fff, $alpha: 0.65);
	overflow: auto;

	.information-subscribe-button {
		padding-top: 12px;
		padding-bottom: 12px;
		font-size: 18px;
	}
}
</style>
